import React, { useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GridApi,
  ICellEditorParams,
  ValueFormatterParams,
  ValueSetterParams,
} from "ag-grid-community";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../components/utils";
import { formatDisplayDate } from "../../../utils/date";
import {
  formatCurrency,
  getCurrencyType,
} from "../../../utils/currencyFormatter";
import { EmployeeMetaDataRow } from "../../../types/Employee";
import SearchInput from "../../../components/shared/SearchInput";
import { NOMINEE_RELATIONSHIP_OPTIONS } from "../../../constants/NomineeConstants";
import { maskPii } from "../../../utils/piiCrypto";

interface EmployeeMetaDataPreviewProps {
  data: EmployeeMetaDataRow[];
  onCancel: () => void;

  onUpdate: (selectedData: EmployeeMetaDataRow[]) => void;
  isImporting?: boolean;
  errorMessage?: string;
}

const DiffCellRenderer = (params: any) => {
  const field = params.colDef?.field;
  const fullData = params.data?.[field];

  const isDateField =
    field === "dateOfJoining" ||
    (typeof field === "string" && field.endsWith(".dob"));

  const oldVal = params.getOldVal
    ? params.getOldVal(params.data)
    : fullData?.old;
  const newVal = params.value;

  const formatVal = (val: any) => {
    if (val === undefined || val === null || val === "" || val === "-")
      return "-";
    if (isDateField && val) {
      return formatDisplayDate(val);
    }
    if (params.colDef?.field === "salary" && val && val !== "-") {
      return formatCurrency(Number(val), getCurrencyType());
    }
    if (Array.isArray(val)) {
      return val.join(", ");
    }
    if (typeof val === "object" && !(val instanceof Date)) {
      return JSON.stringify(val);
    }
    if (typeof val === "boolean" || val === "true" || val === "false") {
      return val === true || val === "true" ? "true" : "false";
    }
    return val === "-" ? "-" : val;
  };

  // Helper to compare values
  const hasChanged = () => {
    if (isDateField) {
      const getFormattedDate = (val: any) => {
        if (!val) return "";
        try {
          const date = new Date(val);
          if (Number.isNaN(date.getTime())) return "";
          return formatDisplayDate(date);
        } catch (e) {
          return "";
        }
      };

      const oldDateStr = getFormattedDate(oldVal);
      const newDateStr = getFormattedDate(newVal);

      if (!oldDateStr || !newDateStr) return oldDateStr !== newDateStr;

      return oldDateStr !== newDateStr;
    }
    // eslint-disable-next-line eqeqeq
    return newVal != oldVal;
  };

  const isChanged = hasChanged();

  return (
    <div className="flex flex-col justify-center h-full leading-tight py-1">
      {isChanged &&
        newVal !== undefined &&
        newVal !== null &&
        newVal !== "" && (
          <div className="text-xs text-gray-400 line-through decoration-red-400">
            {formatVal(oldVal)}
          </div>
        )}
      {isChanged ? (
        <div
          className={`text-sm font-medium ${
            isChanged ? "text-green-600" : "text-gray-900"
          }`}
        >
          {formatVal(newVal)}
        </div>
      ) : (
        <div className="text-sm text-gray-900">{formatVal("-")}</div>
      )}
    </div>
  );
};

const EmployeeMetaDataPreview: React.FC<EmployeeMetaDataPreviewProps> = ({
  data,
  onCancel,
  onUpdate: onImport,
  isImporting = false,
  errorMessage = "",
}) => {
  const gridApi = useRef<GridApi | any>(null);

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    params.api.sizeColumnsToFit();
  };

  const [selectedRows, setSelectedRows] = useState<EmployeeMetaDataRow[]>([]);

  const valueSetter = (params: ValueSetterParams) => {
    const field = params.colDef.field;
    if (!field) return false;

    if (field === "email" || field === "personalEmail") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!params.newValue || !emailRegex.test(params.newValue)) {
        toast.error("Valid Email is required");
        return false;
      }
    }

    const currentObj = params.data[field] || { old: undefined, new: undefined };

    // If value hasn't changed, don't update
    if (currentObj.new === params.newValue) return false;

    const updatedObj = {
      ...currentObj,
      new: params.newValue,
    };

    params.data[field] = updatedObj;
    return true;
  };

  // 2. Value Formatter: When editing, show the 'new' value in the input
  const valueFormatter = (params: ValueFormatterParams) => {
    if (params.value && typeof params.value === "object") {
      return params.value.new;
    }
    return params.value;
  };

  // 3. Cell Editor Selector: Choose editor based on field type
  const cellEditorSelector = (params: ICellEditorParams) => {
    const field = params.colDef.field;

    if (field && params.data[field]?.values) {
      return {
        component: "agSelectCellEditor",
        params: { values: params.data[field].values },
      };
    }

    if (field === "dateOfJoining" || (field && field.endsWith(".dob"))) {
      return {
        component: "agDateCellEditor",
        params: {
          min: "1900-01-01",
          max: "2100-12-31",
        },
      };
    }
    if (
      field === "salary" ||
      field === "surchargePercentage" ||
      field === "cessPercentage"
    ) {
      return { component: "agNumberCellEditor" };
    }
    if (field === "isNewRegime" || field === "keyPerson") {
      return {
        component: "agSelectCellEditor",
        params: { values: ["true", "false"] },
      };
    }
    return { component: "agTextCellEditor" };
  };

  const showGuardianColumns = useMemo(() => {
    const isMinor = (dob: any) => {
      if (!dob) return false;
      const date = new Date(dob);
      if (Number.isNaN(date.getTime())) return false;
      const today = new Date();
      let age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < date.getDate())
      ) {
        age -= 1;
      }
      return age < 18;
    };

    const hasGuardianData = (guardian: any) => {
      if (!guardian) return false;
      return [
        guardian.name,
        guardian.relationship,
        guardian.contact,
        guardian.email,
        guardian.dob,
        guardian.address,
      ].some((val) => val !== undefined && val !== null && val !== "");
    };

    return data.some((row) => {
      const nominees = row.nominees;
      if (!nominees) return false;
      const nomineeKeys = ["nominee1", "nominee2"] as const;
      return nomineeKeys.some((key) => {
        const nomineeNew = nominees.new?.[key];
        const nomineeOld = nominees.old?.[key];
        return (
          isMinor(nomineeNew?.dob) ||
          isMinor(nomineeOld?.dob) ||
          hasGuardianData(nomineeNew?.guardian) ||
          hasGuardianData(nomineeOld?.guardian)
        );
      });
    });
  }, [data]);

  const customFieldKeys = useMemo(() => {
    const keysSet = new Set<string>();
    data.forEach((row) => {
      const customFieldValues = row.customFieldValues;
      if (customFieldValues && typeof customFieldValues === "object") {
        Object.keys(customFieldValues).forEach((key) => keysSet.add(key));
      }
    });
    return Array.from(keysSet);
  }, [data]);

  const columnDefs: ColDef[] = useMemo(() => {
    const keys = [
      "hrId",
      "name",
      "email",
      "personalEmail",
      "designation",
      "businessUnit",
      "grade",
      "band",
      "keyPerson",
      "address",
      "type",
      "dateOfJoining",
      "subsidiaryCompany",
      "manager",
      "contact",
      "pan",
      "department",
      "residentialStatus",
      "nationality",
      "workingCountry",
      "salary",
      "isNewRegime",
      "taxSlab",
      "surchargePercentage",
      "cessPercentage",
    ];

    const getNestedValue = (obj: any, path: string[]) =>
      path.reduce((acc, key) => (acc ? acc[key] : undefined), obj);

    const setNomineeValue = (
      data: any,
      nominee: "nominee1" | "nominee2",
      propPath: string,
      value: any
    ) => {
      const nomineesObj = data.nominees || { old: {}, new: {} };
      const currentNew = nomineesObj.new || {};
      const currentNominee = currentNew[nominee] || {};

      if (propPath.endsWith("email") && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          toast.error("Valid Email is required");
          return false;
        }
      }

      const pathParts = propPath.split(".");
      const existingValue = getNestedValue(currentNominee, pathParts);

      if (existingValue === value) return false;

      let updatedNominee = { ...currentNominee };

      if (pathParts.length === 1) {
        updatedNominee = { ...currentNominee, [pathParts[0]]: value };
      } else {
        const [root, ...rest] = pathParts;
        const currentRoot = currentNominee[root] || {};
        const updatedRoot = { ...currentRoot, [rest[0]]: value };
        updatedNominee = { ...currentNominee, [root]: updatedRoot };
      }

      const updatedNew = { ...currentNew, [nominee]: updatedNominee };

      data.nominees = { ...nomineesObj, new: updatedNew };
      return true;
    };

    const createNomineeCol = (
      nominee: "nominee1" | "nominee2",
      propPath: string,
      header: string,
      isDate = false
    ): ColDef => ({
      headerName: header,
      field: `${nominee}.${propPath}`,
      editable: true,
      cellRenderer: DiffCellRenderer,
      minWidth: 150,
      ...(propPath.endsWith("relationship")
        ? {
            cellEditor: "agRichSelectCellEditor",
            cellEditorParams: {
              allowTyping: true,
              filterList: true,
              highlightMatch: true,
              valueListMaxWidth: 150,
              values: NOMINEE_RELATIONSHIP_OPTIONS,
            },
          }
        : {}),
      valueGetter: (p) => {
        const nomineeData = p.data.nominees?.new?.[nominee];
        if (!nomineeData) return undefined;
        const val = propPath
          .split(".")
          .reduce(
            (acc: any, key: string) => (acc ? acc[key] : undefined),
            nomineeData
          );
        if (isDate) {
          if (!val) return null;
          return Number.isNaN(new Date(val).getTime()) ? null : val;
        }
        return val;
      },
      cellRendererParams: {
        getOldVal: (d: any) => {
          const nomineeData = d.nominees?.old?.[nominee];
          if (!nomineeData) return undefined;
          return propPath
            .split(".")
            .reduce(
              (acc: any, key: string) => (acc ? acc[key] : undefined),
              nomineeData
            );
        },
      },
      valueSetter: (p) =>
        setNomineeValue(p.data, nominee, propPath, p.newValue),
      cellClass: "flex items-center",
    });

    const createCustomFieldCol = (key: string): ColDef => ({
      headerName: key.replace(/_/g, " ").toUpperCase(),
      field: `customFieldValues.${key}`,
      editable: true,
      cellRenderer: DiffCellRenderer,
      valueGetter: (p: any) => p.data.customFieldValues?.[key]?.new,
      cellRendererParams: {
        getOldVal: (d: any) => d.customFieldValues?.[key]?.old,
      },
      valueSetter: (p) => {
        const customFieldValues = p.data.customFieldValues || {};
        const currentObj = customFieldValues[key] || {
          old: undefined,
          new: undefined,
        };
        if (currentObj.new === p.newValue) return false;
        p.data.customFieldValues = {
          ...customFieldValues,
          [key]: { ...currentObj, new: p.newValue },
        };
        return true;
      },
      minWidth: 180,
      autoHeight: true,
      cellClass: "flex items-center",
    });

    const createDematAccountCol = (index: number): ColDef => ({
      headerName: `DEMAT ACCOUNT ID ${index + 1}`,
      field: `dematAccounts.${index}.dematAccountId`,
      editable: true,
      cellRenderer: DiffCellRenderer,
      valueGetter: (params: any) =>
        params.data.dematAccounts?.new?.[index]?.dematAccountId || "",
      cellRendererParams: {
        getOldVal: (row: EmployeeMetaDataRow) =>
          row.dematAccounts?.old?.[index]?.dematAccountId || "",
      },
      valueSetter: (params: any) => {
        const current = params.data.dematAccounts || {
          old: [],
          new: [],
          replace: false,
        };
        const next = [...(current.new || [])];
        const currentAccount = next[index] || {
          dematAccountId: "",
          type: "CDSL",
          status: "active",
          isUserUploaded: false,
        };
        const dematAccountId = String(params.newValue || "").trim();
        if (currentAccount.dematAccountId === dematAccountId) return false;
        next[index] = {
          ...currentAccount,
          dematAccountId,
          type: dematAccountId.startsWith("IN") ? "NSDL" : "CDSL",
        };
        params.data.dematAccounts = { ...current, new: next, replace: true };
        return true;
      },
      minWidth: 180,
      autoHeight: true,
      cellClass: "flex items-center",
    });

    const getHeaderName = (key: string) => {
      let headerName = key.replace(/([A-Z])/g, " $1").toUpperCase();
      if (
        key.toLowerCase().includes("percentage") ||
        key.toLowerCase().includes("slab")
      ) {
        headerName = headerName.replace("PERCENTAGE", "%");
        headerName = headerName.replace("SLAB", "%");
      }
      return headerName;
    };

    return [
      {
        headerName: "HR ID (Existing)",
        field: "hrId",
        pinned: "left",
        valueGetter: (params: any) => params.data.hrId?.old,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 150,
        editable: false,
      },
      ...keys.map((key) => {
        const colDef: ColDef = {
          headerName: getHeaderName(key),
          field: key,
          editable: true,
          cellRenderer: DiffCellRenderer,
          valueGetter: (params: any) => params.data[key]?.new,
          valueSetter,
          cellEditorSelector,
          minWidth: 180,
          autoHeight: true,
          cellClass: "flex items-center",
        };

        if (key === "pan") {
          colDef.cellRendererParams = {
            getOldVal: (d: any) => maskPii(d[key]?.old),
          };
        }

        if (key === "dateOfJoining") {
          colDef.valueGetter = (params: any) => {
            const val = params.data[key]?.new;
            if (!val) return null;
            return Number.isNaN(new Date(val).getTime()) ? null : val;
          };
          colDef.filter = "agDateColumnFilter";
        }

        if (
          key === "salary" ||
          key === "surchargePercentage" ||
          key === "cessPercentage"
        ) {
          colDef.valueGetter = (params: any) => {
            const val = params.data[key]?.new;
            if (val === undefined || val === null || val === "") return null;
            const num = Number(val);
            return Number.isNaN(num) ? null : num;
          };
          colDef.filter = "agNumberColumnFilter";
        }

        return colDef;
      }),
      ...customFieldKeys.map((key) => createCustomFieldCol(key)),
      createDematAccountCol(0),
      createDematAccountCol(1),
      createNomineeCol("nominee1", "name", "NOMINEE 1 NAME"),
      createNomineeCol("nominee1", "relationship", "NOMINEE 1 RELATION"),
      createNomineeCol("nominee1", "percentage", "NOMINEE 1 %"),
      createNomineeCol("nominee1", "dob", "NOMINEE 1 DOB", true),
      createNomineeCol("nominee1", "address", "NOMINEE 1 ADDRESS"),
      createNomineeCol("nominee1", "contact", "NOMINEE 1 CONTACT"),
      createNomineeCol("nominee1", "email", "NOMINEE 1 EMAIL"),
      ...(showGuardianColumns
        ? [
            createNomineeCol("nominee1", "guardian.name", "GUARDIAN 1 NAME"),
            createNomineeCol(
              "nominee1",
              "guardian.relationship",
              "GUARDIAN 1 RELATION"
            ),
            createNomineeCol(
              "nominee1",
              "guardian.contact",
              "GUARDIAN 1 CONTACT"
            ),
            createNomineeCol("nominee1", "guardian.email", "GUARDIAN 1 EMAIL"),
            createNomineeCol(
              "nominee1",
              "guardian.dob",
              "GUARDIAN 1 DOB",
              true
            ),
            createNomineeCol(
              "nominee1",
              "guardian.address",
              "GUARDIAN 1 ADDRESS"
            ),
          ]
        : []),
      createNomineeCol("nominee2", "name", "NOMINEE 2 NAME"),
      createNomineeCol("nominee2", "relationship", "NOMINEE 2 RELATION"),
      createNomineeCol("nominee2", "percentage", "NOMINEE 2 %"),
      createNomineeCol("nominee2", "dob", "NOMINEE 2 DOB", true),
      createNomineeCol("nominee2", "address", "NOMINEE 2 ADDRESS"),
      createNomineeCol("nominee2", "contact", "NOMINEE 2 CONTACT"),
      createNomineeCol("nominee2", "email", "NOMINEE 2 EMAIL"),
      ...(showGuardianColumns
        ? [
            createNomineeCol("nominee2", "guardian.name", "GUARDIAN 2 NAME"),
            createNomineeCol(
              "nominee2",
              "guardian.relationship",
              "GUARDIAN 2 RELATION"
            ),
            createNomineeCol(
              "nominee2",
              "guardian.contact",
              "GUARDIAN 2 CONTACT"
            ),
            createNomineeCol("nominee2", "guardian.email", "GUARDIAN 2 EMAIL"),
            createNomineeCol(
              "nominee2",
              "guardian.dob",
              "GUARDIAN 2 DOB",
              true
            ),
            createNomineeCol(
              "nominee2",
              "guardian.address",
              "GUARDIAN 2 ADDRESS"
            ),
          ]
        : []),
    ];
  }, [showGuardianColumns, customFieldKeys]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      cellStyle: (params) => {
        const baseStyle = { paddingTop: "15px", lineHeight: "20px" };
        if (params.node.rowPinned) {
          return {
            ...baseStyle,
            backgroundColor: "#f0f0f0",
          };
        }
        return baseStyle;
      },
      filterParams: {
        buttons: [],
      },
      menuTabs: [],
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  const handleImport = () => {
    const selectedRows = gridApi.current?.getSelectedRows();
    if (selectedRows && selectedRows.length > 0) {
      onImport(selectedRows);
    }
  };

  return (
    <VStack className="w-full h-full px-4 bg-white border-2 rounded-md min-h-[600px]">
      <HStack className="py-4 text-lg font-medium text-left bg-white border-b justify-between items-center">
        <h6>Preview Employee Updates</h6>
        <HStack className="items-center gap-4 ">
          <SearchInput
            className=""
            placeholder="Search"
            onChange={(e: any) => {
              if (gridApi.current) {
                gridApi.current.setQuickFilter(e.target.value);
              }
            }}
          />
        </HStack>
      </HStack>

      {errorMessage && (
        <div className="p-2 mb-4 text-red-600 bg-red-100 border border-red-400 rounded">
          <ul>
            {errorMessage.split("\n").map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="py-4 ag-theme-material" style={{ height: 500 }}>
        <AgGridReact
          key={"employee-meta-data-grid"}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          rowSelection={"multiple"}
          rowData={data}
          onSelectionChanged={() => {
            const selected = gridApi.current?.getSelectedRows();
            setSelectedRows(selected);
          }}
          rowClass={"border-t border-dashed"}
          suppressRowClickSelection={true}
          stopEditingWhenCellsLoseFocus={true}
          rowMultiSelectWithClick={true}
          singleClickEdit={true}
        />
      </div>

      <HStack className="justify-end gap-4 p-4 border-t">
        <ButtonPrimary1
          className="text-red-500 border-red-500 hover:bg-red-50"
          onClick={onCancel}
          disabled={isImporting}
        >
          Discard & Back
        </ButtonPrimary1>
        <ButtonPrimary
          onClick={handleImport}
          disabled={selectedRows.length === 0}
          loading={isImporting}
        >
          Import Selected
        </ButtonPrimary>
      </HStack>
    </VStack>
  );
};

export default EmployeeMetaDataPreview;
